import { LoadingSkeleton } from "./LoadingSkeleton";
import { ValidatedForm } from "./ValidatedForm";
import { FieldRequirement } from "./FieldRequirement";
import { useEffect, useRef, useState } from "react";
import { ApiError, request } from "../api/client";
import { useLocale } from "../i18n";
import { ErrorNotice } from "./AdminFeedback";
import { UserPlusIcon } from "@heroicons/react/24/outline";
import "../styles/registration-policy.css";
type Policy = { registrationEnabled: boolean; version: number };
export function RegistrationPolicy() {
  const { w } = useLocale();
  const [policy, setPolicy] = useState<Policy>();
  const [enabled, setEnabled] = useState(false);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<unknown>();
  const [busy, setBusy] = useState(false);
  const [revision, setRevision] = useState(0);
  const [notice, setNotice] = useState(false);
  const pending = useRef<object | null>(null);
  const running = useRef(false);
  useEffect(() => {
    const c = new AbortController(); setPolicy(undefined); setError(undefined);
    void request<Policy>("/registrationPolicy", "GET", undefined, c.signal).then(value => {
      if (!c.signal.aborted) { setPolicy(value); setEnabled(value.registrationEnabled); }
    }).catch(e => { if (!c.signal.aborted) setError(e); });
    return () => c.abort();
  }, [revision]);
  async function save() {
    if (!policy || running.current) return;
    running.current = true; setBusy(true); setError(undefined); setNotice(false);
    pending.current ??= {requestId: crypto.randomUUID(), expectedVersion: policy.version, registrationEnabled: enabled, reason: reason.trim()};
    try {
      const result = await request<Policy>("/registrationPolicy", "POST", pending.current);
      pending.current = null; setPolicy(result); setEnabled(result.registrationEnabled); setReason(""); setNotice(true);
    } catch (e) {
      setError(e);
      if (e instanceof ApiError && e.status < 500) { pending.current = null; if (e.status === 409) setPolicy(undefined); }
    } finally { running.current = false; setBusy(false); }
  }
  const changed = !!policy && enabled !== policy.registrationEnabled;
  return <section className="panel registration-policy" aria-labelledby="registration-policy-title">
    <header className="registration-policy-heading">
      <span className="registration-policy-icon"><UserPlusIcon aria-hidden="true" /></span>
      <div><h2 id="registration-policy-title">{w("إنشاء الحسابات الجديدة", "New account registration")}</h2>
      <p>{w("تحكّم بإتاحة إنشاء حسابات جديدة على مزن.", "Control whether new accounts can be created on MZN.")}</p></div>
      {policy && <span className={`registration-policy-status${policy.registrationEnabled ? " is-enabled" : ""}`}>{policy.registrationEnabled ? w("التسجيل متاح", "Registration open") : w("التسجيل متوقف", "Registration paused")}</span>}
    </header>
    <div className="registration-policy-feedback">
    <ErrorNotice error={error} retry={() => { if (!busy && !pending.current) setRevision(v => v + 1); }}/>
    {notice && <p className="success-box" role="status">{w("تم حفظ الإعداد. يحدّث التطبيق خيار التسجيل تلقائياً.", "Setting saved. The app refreshes registration availability automatically.")}</p>}
    {!policy && !error && <LoadingSkeleton variant="form" label={w("جارٍ تحميل الإعداد…", "Loading setting…")} />}
    </div>
    {policy && <ValidatedForm onSubmit={e => {e.preventDefault(); void save();}}>
      <fieldset disabled={busy || !!pending.current} className="registration-policy-fields">
        <div className="registration-policy-option">
          <div><label htmlFor="registration-allowed"><span className="field-caption">{w("السماح بإنشاء حسابات جديدة", "Allow new accounts")}<FieldRequirement required={false} /></span></label>
          <p id="registration-effect">{enabled ? w("يظهر خيار إنشاء الحساب في التطبيق.", "The app shows the create-account option.") : w("يُخفى خيار إنشاء الحساب، وتُمنع محاولات التسجيل الجديدة.", "The create-account option is hidden and new registrations are blocked.")}</p></div>
          <input id="registration-allowed" className="registration-policy-toggle" type="checkbox" aria-describedby="registration-effect" checked={enabled} onChange={e => setEnabled(e.target.checked)}/>
        </div>
        <p className="registration-policy-note">{w("يمكن لأصحاب الحسابات الحالية تسجيل الدخول حتى عند إيقاف التسجيل.", "Existing account holders can still sign in when registration is paused.")}</p>
        <div className="registration-policy-reason">
          <label htmlFor="registration-reason"><span className="field-caption">{w("سبب التغيير", "Reason for change")}<FieldRequirement required={true} /></span></label>
          <textarea id="registration-reason" value={reason} onChange={e => setReason(e.target.value)} maxLength={500} required rows={3} aria-describedby="registration-reason-help" placeholder={w("وضّح سبب إيقاف التسجيل أو إعادة إتاحته…", "Explain why registration is being paused or reopened…")}/>
          <div className="registration-policy-hint"><span id="registration-reason-help">{w("مطلوب لحفظ التغيير، ويُسجّل في سجل الإجراءات.", "Required to save the change and recorded in the audit log.")}</span><bdi>{reason.length} / 500</bdi></div>
        </div>
      </fieldset>
      <footer className="registration-policy-footer"><p aria-live="polite">{changed ? w("لديك تغيير غير محفوظ", "You have an unsaved change") : w("لا توجد تغييرات لحفظها", "No changes to save")}</p>
      <button className="primary" disabled={busy || enabled === policy.registrationEnabled}>{busy ? w("جارٍ الحفظ…", "Saving…") : w("حفظ الإعداد", "Save setting")}</button>
      </footer>
    </ValidatedForm>}
  </section>;
}
